// src/components/ParticipantRegistrationModal.js
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  Chip,
  useTheme, // New import for responsiveness
  useMediaQuery, // New import for responsiveness
} from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import CloseIcon from '@mui/icons-material/Close';

const initialState = {
  Title: 'Mr',
  FirstName: '',
  LastName: '',
  Gender: 'Male',
  PhoneNumber: '',
  EmailAddress: '',
  Organization: '',
  CountryName: '',
  DelegateType: '',
};

const ParticipantRegistrationModal = ({ open, onClose, onRegistrationSuccess }) => {
  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Check for mobile screen size

  useEffect(() => {
    if (!open) {
      setFormData(initialState);
      setError('');
      setLoading(false);
    }
  }, [open]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await api.post('/api/participants/register-onsite', formData);
      onRegistrationSuccess(response.data.ParticipantID);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddAlt1Icon />
          <Typography variant="h6" component="div">On-Site Participant Registration</Typography>
        </Box>
        <IconButton aria-label="close" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box component="form" id="registration-form" onSubmit={handleSubmit} sx={{ pt: 2 }}>
          
          <Divider sx={{ mb: 3 }}><Chip label="Personal Information" /></Divider>
          
          {/* [-- THIS IS THE FIX --] Using responsive props in the `sx` prop */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 120 }} required>
              <InputLabel>Title</InputLabel>
              <Select name="Title" value={formData.Title} label="Title" onChange={handleChange}>
                <MenuItem value="Mr">Mr</MenuItem><MenuItem value="Mrs">Mrs</MenuItem><MenuItem value="Ms">Ms</MenuItem>
                <MenuItem value="Dr">Dr</MenuItem><MenuItem value="Prof">Prof</MenuItem>
              </Select>
            </FormControl>
            <TextField name="FirstName" label="First Name" value={formData.FirstName} onChange={handleChange} fullWidth required />
            <TextField name="LastName" label="Last Name" value={formData.LastName} onChange={handleChange} fullWidth required />
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Gender</InputLabel>
              <Select name="Gender" value={formData.Gender} label="Gender" onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem><MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
            <TextField name="PhoneNumber" label="Phone Number" value={formData.PhoneNumber} onChange={handleChange} fullWidth />
          </Box>

          <Divider sx={{ my: 3 }}><Chip label="Contact & Professional Information" /></Divider>
          
          <TextField name="EmailAddress" label="Email Address" type="email" value={formData.EmailAddress} onChange={handleChange} fullWidth required sx={{ mb: 2 }}/>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <TextField name="Organization" label="Organization" value={formData.Organization} onChange={handleChange} fullWidth />
            <TextField name="CountryName" label="Country" value={formData.CountryName} onChange={handleChange} fullWidth />
          </Box>

          <TextField name="DelegateType" label="Delegate Type (e.g., Speaker, Guest)" value={formData.DelegateType} onChange={handleChange} fullWidth />
          
          {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" form="registration-form" variant="contained" disabled={loading} sx={{ minWidth: 200 }}>
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Register & Proceed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ParticipantRegistrationModal;